/**
 * DocumentsListPage — shared list view for non-invoice doc types.
 *
 * Renders the list for Quotes (`doc_type=quote`), Credit Notes (`credit_note`),
 * and Debit Notes (`debit_note`) using a single component. All three doc
 * types share the same DB collection and endpoints under the hood; only the
 * URL, terminology, and detail-page routes differ.
 *
 * Sprint 2026-07 Sprint 2 (doc_type discriminator revamp).
 */
import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, FileText, ArrowRight } from 'lucide-react';
import { api, fmtMoney, fmtDate } from '../../lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { StatusBadge } from './InvoicingDashboard';

// Doc-type meta so a single component can render 3 different pages.
export const DOC_TYPE_META = {
    quote: {
        title: 'Quotes / Estimates',
        subtitle: 'Send a quote first, then convert to invoice on approval.',
        singular: 'quote',
        newLabel: 'New quote',
        route: 'quotes',
        prefix: 'QT',
    },
    credit_note: {
        title: 'Credit Notes',
        subtitle: 'Issue credit notes for returns, discounts or invoice corrections. GST-compliant.',
        singular: 'credit note',
        newLabel: 'New credit note',
        route: 'credit-notes',
        prefix: 'CN',
    },
    debit_note: {
        title: 'Debit Notes',
        subtitle: 'Charge back customers for undercharged amounts or extra fees.',
        singular: 'debit note',
        newLabel: 'New debit note',
        route: 'debit-notes',
        prefix: 'DN',
    },
    // Sprint 2026-09: three additional document types for the full B2B
    // commercial paper trail. All four share the same list component +
    // editor + PDF template; only the labels change.
    delivery_challan: {
        title: 'Delivery Challans',
        subtitle: 'Goods dispatched without an invoice yet — customer signs the challan on receipt.',
        singular: 'delivery challan',
        newLabel: 'New delivery challan',
        route: 'delivery-challans',
        prefix: 'DC',
    },
    sales_order: {
        title: 'Sales Orders',
        subtitle: "Confirm what the customer ordered before billing — convert to invoice on dispatch.",
        singular: 'sales order',
        newLabel: 'New sales order',
        route: 'sales-orders',
        prefix: 'SO',
    },
    purchase_order: {
        title: 'Purchase Orders',
        subtitle: 'Send an order to your supplier before goods are shipped.',
        singular: 'purchase order',
        newLabel: 'New purchase order',
        route: 'purchase-orders',
        prefix: 'PO',
    },
};

export default function DocumentsListPage({ docType }) {
    const meta = DOC_TYPE_META[docType];
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [status, setStatus] = useState('all');
    const nav = useNavigate();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: 100 };
            if (status && status !== 'all') params.status = status;
            if (q) params.q = q;
            const r = await api.invListDocs(docType, params);
            setItems(r.items || []);
        } catch (e) {
            toast.error(`Failed to load ${meta.singular}s`);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docType, status]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-4" data-testid={`${meta.route}-list-page`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-2xl font-heading font-semibold tracking-tight">{meta.title}</h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-xl">{meta.subtitle}</p>
                </div>
                <Button asChild data-testid="btn-new-doc">
                    <Link to={`/invoicing/${meta.route}/new`}>
                        <Plus size={16} className="mr-1.5" /> {meta.newLabel}
                    </Link>
                </Button>
            </div>

            <Card>
                <CardHeader className="pb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="relative flex-1 min-w-[220px]">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                data-testid="doc-search"
                                placeholder={`${meta.singular} # or customer...`}
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
                                className="pl-9"
                            />
                        </div>
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger data-testid="doc-status-filter" className="w-[150px]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All statuses</SelectItem>
                                <SelectItem value="draft">Draft</SelectItem>
                                <SelectItem value="issued">Issued</SelectItem>
                                {docType === 'quote' && <SelectItem value="converted">Converted</SelectItem>}
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                        </Select>
                        <Button variant="outline" size="sm" onClick={load} data-testid="doc-search-btn">Search</Button>
                    </div>
                </CardHeader>

                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-6"><Skeleton className="h-40 w-full" /></div>
                    ) : items.length === 0 ? (
                        <div className="text-center py-16 px-4" data-testid="doc-empty">
                            <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                                <FileText size={26} className="text-primary" />
                            </div>
                            <div className="font-medium mb-1">No {meta.singular}s yet</div>
                            <div className="text-sm text-muted-foreground mb-4">
                                {docType === 'quote'
                                    ? 'Draft a quote, share the PDF with your customer, and turn it into an invoice on acceptance.'
                                    : docType === 'credit_note'
                                    ? 'Issue a credit note when a customer returns goods or you\'ve overcharged them.'
                                    : 'Use debit notes to charge additional amounts against a previously issued invoice.'}
                            </div>
                            <Button asChild data-testid="doc-empty-cta">
                                <Link to={`/invoicing/${meta.route}/new`}>
                                    <Plus size={14} className="mr-1" /> Create your first {meta.singular}
                                </Link>
                            </Button>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{meta.prefix} #</TableHead>
                                    <TableHead>Customer</TableHead>
                                    <TableHead>Date</TableHead>
                                    <TableHead className="text-right">Amount</TableHead>
                                    <TableHead className="text-right">Status</TableHead>
                                    <TableHead className="w-8"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {items.map((doc) => (
                                    <TableRow
                                        key={doc.id}
                                        onClick={() => nav(`/invoicing/${meta.route}/${doc.id}`)}
                                        className="cursor-pointer hover:bg-accent/40"
                                        data-testid={`doc-row-${doc.id}`}
                                    >
                                        <TableCell className="font-mono text-xs">
                                            {doc.invoice_number || <span className="text-muted-foreground italic">DRAFT</span>}
                                        </TableCell>
                                        <TableCell className="font-medium">{doc.customer_snapshot?.name || '—'}</TableCell>
                                        <TableCell className="text-xs text-muted-foreground">{fmtDate(doc.invoice_date)}</TableCell>
                                        <TableCell className="text-right tabular-nums">{fmtMoney(doc.grand_total)}</TableCell>
                                        <TableCell className="text-right">
                                            <StatusBadge status={doc.status} />
                                        </TableCell>
                                        <TableCell><ArrowRight size={14} className="text-muted-foreground" /></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
